// Role → permission map. Derived from spec §5 permission matrix,
// extended with the MANAGER level + per-user overrides (Accès & rôles).
// Permission strings are referenced by `requirePermission(...)` middleware on the server.
export const ROLES = ['BOSS', 'ADMIN', 'MANAGER', 'ASSISTANT', 'EMPLOYEE', 'RECRUTEUR'];
// Back-office roles. EMPLOYEE is the Espace employé self-service role:
// it holds no staff permission and is blocked from every staff router
// server-side (requireStaff) — it can only reach /api/auth and /api/me.
export const STAFF_ROLES = ['BOSS', 'ADMIN', 'MANAGER', 'ASSISTANT'];
export const isStaffRole = (role) => STAFF_ROLES.includes(role);
// Manager recruteur : un compte hors back-office qui n'atteint que le
// module Recrutement, et seulement les postes dont il est le manager
// recruteur (server/src/lib/recScope.ts). Invité depuis Paramètres →
// Recrutement. Comme EMPLOYEE, requireStaff le bloque partout ailleurs.
export const RECRUITER_ROLE = 'RECRUTEUR';
/** Rôles qu'on peut inviter / attribuer depuis Paramètres. */
export const INVITABLE_ROLES = [...STAFF_ROLES, RECRUITER_ROLE];
export const PERMISSIONS = {
    'attendance.edit': ['BOSS', 'ADMIN', 'MANAGER', 'ASSISTANT'],
    // Second signature on a day. attendance.edit fills the day in and signs
    // the first level; this one reviews that work and closes the day — the
    // moment the leave ledger moves and the digest goes out. It is also the
    // only key that can still edit a day under review, or reopen a closed
    // one. Deliberately narrower than attendance.edit: the point of two
    // levels is that the same person does not do both.
    'attendance.validate_final': ['BOSS', 'ADMIN'],
    'attendance.import': ['BOSS', 'ADMIN', 'ASSISTANT'],
    // Re-importing the pointeuse over dates that already carry data —
    // punches, an arbitration, a signed-off day. A first import of virgin
    // dates stays open to attendance.import; overwriting what a human has
    // already read and decided is a different act, and a narrower one.
    'attendance.reimport': ['BOSS', 'ADMIN'],
    'email.send': ['BOSS', 'ADMIN', 'MANAGER', 'ASSISTANT'],
    'employees.edit': ['BOSS', 'ADMIN'],
    // Entry ticket to a fiche: the minimum needed to create a person and
    // keep their date de sortie and emergency contacts current. Everyone
    // holding `employees.edit` holds this too — it is the smaller half of
    // the same key, not a rival to it. Granted per user (Paramètres →
    // Accès) to an assistant who does the paperwork without owning the
    // salary, the bank details or the contract.
    'employees.edit_limited': ['BOSS', 'ADMIN'],
    'holidays.edit': ['BOSS', 'ADMIN'],
    'postes.edit': ['BOSS', 'ADMIN'],
    'settings.edit': ['BOSS', 'ADMIN'],
    'reports.export': ['BOSS', 'ADMIN', 'MANAGER'],
    'activity.view': ['BOSS', 'ADMIN'],
    'users.manage': ['BOSS'],
    // Connexions & usage — who signs in, from where, and how long they
    // actually use the app. Owner-level by default (BOSS only, ADMIN
    // deliberately excluded): it reports on colleagues, not on data.
    // Grantable per user from Paramètres → Accès when a DRH needs it.
    'usage.view': ['BOSS'],
    // Payroll (Salaires & Bonus) — ASSISTANT/MANAGER deliberately excluded
    // from all three by default: salaries are confidential. Three keys so
    // export can be restricted separately without touching routes.
    'payroll.view': ['BOSS', 'ADMIN'],
    'payroll.edit': ['BOSS', 'ADMIN'],
    // Second signature on the payroll month. payroll.edit prepares it and
    // hands it over; this one reads that work and unlocks the bank file —
    // the moment the money becomes sendable. It is also the only key that
    // can still correct an amount under review, send the month back to
    // saisie, or waive a missing grille mensuelle. Owner-level by default
    // (BOSS alone, ADMIN deliberately excluded): the point of two levels
    // is that the person who prepares the pay does not release it.
    'payroll.validate': ['BOSS'],
    'payroll.export': ['BOSS', 'ADMIN'],
    // Évaluations annuelles — MANAGER runs the campaign for their scope
    // (entity scoping restricts which employees they see).
    'evaluations.view': ['BOSS', 'ADMIN', 'MANAGER'],
    'evaluations.edit': ['BOSS', 'ADMIN', 'MANAGER'],
    // Recrutement — salary data (fourchettes, montants d'offre) is gated at
    // field level by 'recrutement.salaires', not by screen: a manager keeps
    // the pipeline and scorecards but never sees the amounts (spec §2.6).
    // 'recrutement.valider' is the directeur→owner approval rail; directors
    // receive it as a per-user grant.
    // v2 (2026-10-20): le manager recruteur et l'owner mènent le process
    // (présélection → entretiens); la RH n'entre qu'à l'acceptation de
    // l'offre (recrutement.rh); vérification + offre = recrutement.offre.
    'recrutement.view': ['BOSS', 'ADMIN', 'MANAGER', 'ASSISTANT', 'RECRUTEUR'],
    'recrutement.edit': ['BOSS', 'MANAGER', 'RECRUTEUR'],
    'recrutement.offre': ['BOSS'],
    'recrutement.rh': ['BOSS', 'ADMIN'],
    'recrutement.valider': ['BOSS'],
    'recrutement.salaires': ['BOSS', 'ADMIN'],
    // Éditeur de catalogue: créer des rôles et éditer leurs 11 templates.
    // Octroyable par utilisateur (Paramètres → Accès); les fourchettes
    // restent en plus derrière recrutement.salaires.
    'recrutement.catalogue': ['BOSS', 'ADMIN'],
    // Bureau (office manager) — la journée en moments, les suivis à étapes
    // et le stock. bureau.view ouvre le module et suffit pour tenir la
    // journée ; bureau.manage pilote (validations, réglages, suivi). Pas
    // d'ASSISTANT par défaut : l'office manager reçoit bureau.view par
    // override (Paramètres → Accès) le jour où le module lui est ouvert.
    'bureau.view': ['BOSS', 'ADMIN'],
    'bureau.manage': ['BOSS', 'ADMIN'],
};
export const ALL_PERMISSIONS = Object.keys(PERMISSIONS);
export const PERMISSION_META = {
    'attendance.edit': { group: 'Pointage', label: 'Saisie & validation', desc: 'Saisie quotidienne, grille, congés/récup, demandes de documents.' },
    'attendance.validate_final': { group: 'Pointage', label: 'Validation finale', desc: 'Revoir et clôturer la journée, modifier une journée en revue, rouvrir une journée validée.' },
    'attendance.import': { group: 'Pointage', label: 'Import pointeuse', desc: 'Importer les fichiers de la pointeuse.' },
    'attendance.reimport': { group: 'Pointage', label: 'Ré-import (écrasement)', desc: 'Ré-importer sur des dates qui ont déjà des pointages ou des décisions.' },
    'email.send': { group: 'Pointage', label: 'Envoi d’emails', desc: 'Relances et notifications manuelles.' },
    'employees.edit': { group: 'Données', label: 'Fiches employés', desc: 'Créer et modifier les fiches employés — tous les champs.' },
    'employees.edit_limited': { group: 'Données', label: 'Fiches (limité)', desc: 'Créer une fiche (nom, entité, date d’entrée) et tenir date de sortie + contacts d’urgence. Rien d’autre.' },
    'holidays.edit': { group: 'Données', label: 'Jours fériés', desc: 'Gérer le calendrier des jours fériés.' },
    'postes.edit': { group: 'Données', label: 'Catalogue postes', desc: 'Gérer les intitulés de poste.' },
    'reports.export': { group: 'Données', label: 'Rapports & exports', desc: 'Générer les rapports PDF et attestations.' },
    'payroll.view': { group: 'Paie', label: 'Voir les salaires', desc: 'Consulter Salaires & bonus.' },
    'payroll.edit': { group: 'Paie', label: 'Modifier la paie', desc: 'Saisir salaires, notes et bonus, puis soumettre le mois pour validation.' },
    'payroll.validate': { group: 'Paie', label: 'Valider la paie', desc: 'Valider le mois soumis et débloquer le fichier banque. Seule clé pouvant corriger un montant en revue, renvoyer en saisie ou déroger à une grille non validée.' },
    'payroll.export': { group: 'Paie', label: 'Export bancaire', desc: 'Générer le fichier de virement.' },
    'evaluations.view': { group: 'Talent', label: 'Voir les évaluations', desc: 'Consulter les évaluations annuelles.' },
    'evaluations.edit': { group: 'Talent', label: 'Conduire l’évaluation', desc: 'Noter, arbitrer et relancer les campagnes.' },
    'recrutement.view': { group: 'Talent', label: 'Voir le recrutement', desc: 'Pipeline, candidats et entretiens de son périmètre.' },
    'recrutement.edit': { group: 'Talent', label: 'Mener le recrutement', desc: 'Demandes, présélection, appel, cas pratique, entretiens, points d’essai.' },
    'recrutement.offre': { group: 'Talent', label: 'Vérification & offre', desc: 'Références, documents, intégrité, offre et création de la fiche employé.' },
    'recrutement.rh': { group: 'Talent', label: 'RH recrutement', desc: 'Dossier administratif de la recrue et co-signature de la décision d’essai.' },
    'recrutement.valider': { group: 'Talent', label: 'Valider (recrutement)', desc: 'Approuver demandes et offres — circuit directeur → owner.' },
    'recrutement.salaires': { group: 'Talent', label: 'Salaires recrutement', desc: 'Fourchettes et montants d’offre — champ restreint.' },
    'recrutement.catalogue': { group: 'Talent', label: 'Catalogue rôles (recrutement)', desc: 'Créer des rôles et éditer leurs 12 templates.' },
    'bureau.view': { group: 'Bureau', label: 'Bureau', desc: 'Ouvrir le Bureau : journée, suivis et stock de l’office manager.' },
    'bureau.manage': { group: 'Bureau', label: 'Piloter le Bureau', desc: 'Valider les achats et décisions, confier des suivis, régler moments, stock et factures.' },
    'activity.view': { group: 'Système', label: 'Journal d’activité', desc: 'Consulter l’audit trail complet.' },
    'settings.edit': { group: 'Système', label: 'Paramètres', desc: 'Modifier la configuration de l’application.' },
    'users.manage': { group: 'Système', label: 'Gestion des accès', desc: 'Inviter des utilisateurs, rôles et permissions.' },
    'usage.view': { group: 'Système', label: 'Connexions & usage', desc: 'Voir qui se connecte, depuis quel appareil et combien de temps.' },
};
export function hasPermission(role, permission) {
    return PERMISSIONS[permission].includes(role);
}
export function permissionsForRole(role) {
    return ALL_PERMISSIONS.filter((p) => hasPermission(role, p));
}
// ─── Per-user overrides ──────────────────────────────────────────
// Effective permissions = role defaults + granted − revoked, with two
// hard rails: EMPLOYEE never gains a staff permission (grants are
// ignored — self-service accounts stay out of the back-office), and
// BOSS never loses one (revokes are ignored — the owner cannot lock
// themself out).
export function effectivePermissions(role, granted = [], revoked = []) {
    if (role === 'EMPLOYEE')
        return [];
    const base = new Set(permissionsForRole(role));
    if (role !== 'BOSS') {
        for (const p of granted)
            if (ALL_PERMISSIONS.includes(p))
                base.add(p);
        for (const p of revoked)
            base.delete(p);
    }
    return ALL_PERMISSIONS.filter((p) => base.has(p));
}
export function hasEffectivePermission(user, permission) {
    return effectivePermissions(user.role, user.permsGranted ?? [], user.permsRevoked ?? []).includes(permission);
}
//# sourceMappingURL=permissions.js.map